import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Noimg from "../images/no_img.jpg";
import { getProduct, orderStatus } from "../api/ProductApi.js";
import { currentRegister } from "utility/Constant.js";
import { getBranchId } from "utility/HeaderConfig";
import ProductCommon from "../components/ProductLike&Wish";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import no_data from "images/no_data.png";
import SEO from "components/headers/Seo";

const SORT_OPTIONS = [
  { label: "Newest First", value: "newest" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
];

const METAL_FILTERS = ["Gold", "Silver", "Diamond", "Platinum"];

export default function NewArrivals({ configImages,prodLabel }) {
  const [categoryList, setCategoryList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(12);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [selectedMetals, setSelectedMetals] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [maxPrice, setMaxPrice] = useState(500000);
  const [totalCount, setTotalCount] = useState(0);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  const history = useNavigate();

  useEffect(() => {
    Promise.all([
      orderStatus({ orderStatusName: "sale" }).then((d) => d.response?.[0].id),
      orderStatus({ orderStatusName: "Transferred" }).then(
        (d) => d.response?.[0].id,
      ),
      orderStatus({ orderStatusName: "Sold" }).then((d) => d.response?.[0].id),
    ])
      .then(([sale, transfer, sold]) => getNewArrivals(sale, transfer, sold))
      .catch(console.error);
    //eslint-disable-next-line
  }, [size]);

  const getNewArrivals = (sale, transfer, sold, currentSize = size) => {
    const isLoadMore = !isInitialLoad.current;

    if (isLoadMore) {
      setLoadMoreLoading(true);
    } else {
      setLoading(true);
    }
    if (sale && transfer && sold) {
      getProduct(size, {
        branch: getBranchId(),
        allowCatalogue:1,
        removeNTP: "NTP-", removeHP: "HP-",
        beforeCreatedAt: currentRegister(),
        productDemoStatus: "Demo_Completed,Demo_Cancelled,Available_For_Demo",
        productLabel: "NewArrival",
        statusId: `${sale}, ${transfer}, ${sold}`,
      }).then((data) => {
        let arrivalsData;
        if (data.response && typeof data.response === "string") {
          try {
            const sanitizedResponse = data.response.replace(
              /\bNaN\b/g,
              "null",
            );
            arrivalsData = JSON.parse(sanitizedResponse);
          } catch (parseError) {
            console.error("Failed to parse response:", parseError);
            arrivalsData = { content: [], totalCount: 0 };
          }
        } else {
          arrivalsData = data.response || { content: [], totalCount: 0 };
        }
        setCategoryList(arrivalsData.content || []);
        setTotalCount(arrivalsData.totalCount || 0);

        if (isInitialLoad.current) {
          isInitialLoad.current = false;
        }

        if (isLoadMore) {
          setLoadMoreLoading(false);
        } else {
          setLoading(false);
        }
        const prices = arrivalsData.content.map((i) => Math.trunc(i.total)).filter(Boolean);
        if (prices?.length) {
          const max = Math.max(...prices);
          setMaxPrice(max);
          setPriceRange([0, max]);
        }
        setLoading(false);
      })
        .catch((err) => {
          console.error(err);
          if (isLoadMore) {
            setLoadMoreLoading(false);
          } else {
            setLoading(false);
          }
        });
    }
  };

  const applyFilters = useCallback(() => {
    let result = [...categoryList];
    if (selectedMetals?.length > 0) {
      result = result.filter((i) =>
        selectedMetals.some((m) => (i.metalType || "").toLowerCase().includes(m.toLowerCase()))
      );
    }
    result = result.filter((i) => {
      const price = Math.trunc(i.total);
      return price >= priceRange?.[0] && price <= priceRange?.[1];
    });
    if (sortBy === "price_asc") result.sort((a, b) => a.total - b.total);
    else if (sortBy === "price_desc") result.sort((a, b) => b.total - a.total);
    setFiltered(result);
  }, [categoryList, selectedMetals, priceRange, sortBy]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const imgSrc = (item) =>
    item.images === "@#@" || !item.images ? Noimg : item.images.split("@#@")?.[0];

  const toggleMetal = (m) =>
    setSelectedMetals((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);

  const clearFilters = () => {
    setSelectedMetals([]);
    setPriceRange([0, maxPrice]);
    setSortBy("newest");
  };

  const activeFilterCount = selectedMetals?.length + (priceRange?.[0] > 0 || priceRange?.[1] < maxPrice ? 1 : 0);

  return (
    <div style={{ background: "#faf8fc", minHeight: "100vh", fontFamily: "var(--font-body)" }}>
      <SEO title="New Arrivals — AVM Jewellers" description="Explore our latest jewellery arrivals." name="AVM Jewellers" type="article" />

      {/* Breadcrumb */}
      <div style={{ background: "var(--surface-cream)", padding: "0 24px", height: 64, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#643f6b" }}>
        <Link to="/" style={{ color: "#7517b4", textDecoration: "none", fontWeight: 500 }}>Home</Link>
        <span style={{ color: "#c4b5cd" }}>›</span>
        <span style={{ fontWeight: 600, color: "#4f3267" }}>New Arrivals</span>
      </div>

      {/* Page header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f0e9fa", padding: "28px 24px 20px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: "#8217b4", margin: "0 0 4px" }}>Fresh In</p>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(26px,4vw,38px)", fontWeight: 500, color: "#2e215c", margin: "0 0 4px" }}>{prodLabel.find((item) => item.name === "NewArrival")?.label ||
                "New Arrivals"}</h1>
          {!loading && <p style={{ fontSize: 13, color: "#98989d", margin: 0 }}>{totalCount} pieces</p>}
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 16px 80px", display: "flex", gap: 0, alignItems: "flex-start" }}>

        {/* ── Sidebar ── */}
        <aside style={{
          width: 240, minWidth: 240, flexShrink: 0,
          position: "sticky", top: 72,
          maxHeight: "calc(100vh - 80px)", overflowY: "auto",
          padding: "24px 0",
          scrollbarWidth: "thin", scrollbarColor: "#e9e0ed transparent",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "0 4px" }}>
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: "#13081a" }}>Filters</span>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={{ fontSize: 11, fontWeight: 600, color: "#7517b4", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                Clear All
              </button>
            )}
          </div>

          {/* Sort */}
          <div style={{ border: "1px solid #e9e0ed", borderRadius: 10, overflow: "hidden", marginBottom: 10, background: "#fff" }}>
            <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#13081a", borderBottom: "1px solid #e9e0ed" }}>Sort By</div>
            <div style={{ padding: "4px 14px 10px" }}>
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setSortBy(o.value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 0", border: "none", background: "none",
                    width: "100%", textAlign: "left", cursor: "pointer",
                    fontSize: 12.5, color: sortBy === o.value ? "#4f3267" : "#3a3a3a",
                    fontWeight: sortBy === o.value ? 700 : 400,
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: "50%",
                    border: `2px solid ${sortBy === o.value ? "#bd7af3" : "#ccc"}`,
                    background: sortBy === o.value ? "#bd7af3" : "transparent",
                    flexShrink: 0, transition: "all .15s",
                  }} />
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Metal type */}
          <div style={{ border: "1px solid #e9e0ed", borderRadius: 10, overflow: "hidden", marginBottom: 10, background: "#fff" }}>
            <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#13081a", borderBottom: "1px solid #e9e0ed" }}>Metal</div>
            <div style={{ padding: "4px 14px 10px" }}>
              {METAL_FILTERS.map((m) => (
                <button
                  key={m}
                  onClick={() => toggleMetal(m)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 0", border: "none", background: "none",
                    width: "100%", textAlign: "left", cursor: "pointer",
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: `1.5px solid ${selectedMetals.includes(m) ? "#bd7af3" : "#ccc"}`,
                    background: selectedMetals.includes(m) ? "#bd7af3" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all .15s",
                  }}>
                    {selectedMetals.includes(m) && (
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span style={{ fontSize: 12.5, color: "#3a3a3a" }}>{m}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Price range */}
          <div style={{ border: "1px solid #e9e0ed", borderRadius: 10, overflow: "hidden", marginBottom: 10, background: "#fff" }}>
            <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#13081a", borderBottom: "1px solid #e9e0ed" }}>Price Range</div>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, color: "#4f3267", marginBottom: 10 }}>
                <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
                <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
              </div>
              <input
                type="range" min={0} max={maxPrice} value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                style={{ width: "100%", accentColor: "#bd7af3" }}
              />
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main style={{ flex: 1, padding: "24px 0 0 20px", minWidth: 0 }}>
          {/* Toolbar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {activeFilterCount > 0 && (
                <span style={{ fontSize: 12, background: "#f3ecff", color: "#7517b4", padding: "3px 10px", borderRadius: 999, fontWeight: 600 }}>
                  {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} active
                </span>
              )}
            </div>
            {/* View toggle */}
            <div style={{ display: "flex", gap: 4, border: "1px solid #e9e0ed", borderRadius: 8, overflow: "hidden" }}>
              {["grid", "list"].map((v) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  style={{
                    padding: "6px 10px", border: "none", cursor: "pointer",
                    background: viewMode === v ? "#4f3267" : "#fff",
                    color: viewMode === v ? "#fff" : "#888",
                    transition: "all .15s",
                  }}
                >
                  {v === "grid"
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                    : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="4" width="18" height="3" /><rect x="3" y="10" width="18" height="3" /><rect x="3" y="16" width="18" height="3" /></svg>
                  }
                </button>
              ))}
            </div>
          </div>

          {!loading && filtered?.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <img src={no_data} alt="No products" style={{ maxWidth: 200, margin: "0 auto 16px" }} loading="lazy" />
              <p style={{ fontSize: 15, color: "#c48eeb" }}>No arrivals match your filters.</p>
              <button onClick={clearFilters} style={{ marginTop: 12, padding: "10px 24px", background: "#4f3267", color: "#fff", border: "none", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: viewMode === "grid"
                  ? "repeat(auto-fill, minmax(200px, 1fr))"
                  : "1fr",
                gap: viewMode === "grid" ? 16 : 12,
              }}>
                {filtered?.map((item, i) => (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: Math.min(i * 0.03, 0.3) }}
                    onClick={() => history(`/ProductDetails/${item.id}`, { state: { categoryItem: item } })}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    style={{
                      background: "#fff", borderRadius: 14,
                      border: `1px solid ${hoveredIdx === i ? "#e0c8f8" : "#f2f2f7"}`,
                      boxShadow: hoveredIdx === i ? "0 12px 40px rgba(0,0,0,.1)" : "0 2px 8px rgba(0,0,0,.05)",
                      transform: hoveredIdx === i ? "translateY(-4px)" : "none",
                      transition: "box-shadow .24s, transform .24s, border-color .24s",
                      cursor: "pointer", overflow: "hidden",
                      display: viewMode === "list" ? "flex" : "block",
                    }}
                  >
                    {/* Image */}
                    <div style={{
                      position: "relative",
                      overflow: "hidden",
                      background: "#fafafa",
                      aspectRatio: viewMode === "grid" ? "1" : undefined,
                      width: viewMode === "list" ? 120 : "100%",
                      minWidth: viewMode === "list" ? 120 : undefined,
                      flexShrink: 0,
                    }}>
                      <img
                        src={imgSrc(item)}
                        alt={item.category || "New Arrival"}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: viewMode === "list" ? "100%" : "100%",
                          objectFit: "cover",
                          transform: hoveredIdx === i ? "scale(1.06)" : "scale(1)",
                          transition: "transform .5s cubic-bezier(.16,1,.3,1)",
                          display: "block",
                        }}
                      />
                      {/* Like / Wish */}
                      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
                        <ProductCommon categoryItem={item} />
                      </div>
                      {item.stockStatus === "Sale" ? (
                        <span className="bsc-badge">Available</span>
                      ) : <span className="bsc-soldbadge">Sold</span>}
                    </div>

                    {/* Info */}
                    <div style={{ padding: "14px 16px", flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "#98989d", margin: "0 0 3px" }}>
                        {item.metalType}
                      </p>
                      <p style={{ fontSize: 13, fontWeight: 500, color: "#1c1c1e", margin: "0 0 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.category}>
                        {item.category}
                      </p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span className="pl-card-price">
                          ₹{Math.trunc(item.total).toLocaleString("en-IN")}
                        </span>
                        {item.goldPurity && item.goldPurity !== "-" && (
                          <span style={{ fontSize: 10.5, color: "#98989d" }}>{item.goldPurity}</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Skeleton cards */}
                {loading && Array.from({ length: 8 }).map((_, i) => (
                  <div key={`sk-${i}`} style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #f2f2f7" }}>
                    <Skeleton style={{ height: 200 }} />
                    <div style={{ padding: 14 }}>
                      <Skeleton height={11} style={{ marginBottom: 5, borderRadius: 4 }} />
                      <Skeleton height={13} style={{ marginBottom: 7, borderRadius: 4 }} />
                      <Skeleton height={16} width="40%" style={{ borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>

              {!loading && categoryList?.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 48 }}>
                  <button
                    onClick={() => setSize((s) => s + 10)}
                    disabled={loadMoreLoading || loading}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 36px",
                      background: "#4f3267", color: "#fff",
                      fontSize: 13, fontWeight: 600, letterSpacing: ".06em",
                      border: "none", borderRadius: 999, cursor: "pointer",
                      transition: "background .14s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#7517b4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#4f3267"}
                  >
                    {loadMoreLoading ? "Loading..." : "Load More"}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 768px) {
          .na-sidebar { display: none; }
        }
      `}</style>
    </div>
  );
}